import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import DeleteConfirmDialog from "@/components/DeleteConfirmDialog";
import {
  Route, ArrowLeft, Plus, Pencil, Trash2, ChevronDown, ChevronUp, ChevronRight,
  GripVertical, CheckCircle2, Circle, BookOpen, Sparkles,
  BookMarked, ClipboardList, X, Wand2, Zap, Settings2, Trophy, Lock, Eye, Video, Music,
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "react-hot-toast";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const getInitials = (name) => name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';

const SCRIPTURE_TRANSLATIONS = [
  { id: "esv", label: "ESV", source: "esv-api" },
  { id: "web", label: "WEB", source: "bible-api" },
  { id: "kjv", label: "KJV", source: "bible-api" },
  { id: "asv", label: "ASV", source: "bible-api" },
];

const cleanVerseText = (verse) =>
  (verse?.text || verse?.passages?.[0] || "")
    .split("\n").map((l) => l.trim()).filter(Boolean).join(" ")
    .replace(/\[\d+\]/g, "").replace(/\s{2,}/g, " ").trim();

const CircularProgress = ({ percent, size = 128, stroke = 12 }) => {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }} data-testid="growth-plan-progress-ring">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} stroke="#e2e8f0" strokeWidth={stroke} fill="none" />
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          stroke="url(#gp-gradient)" strokeWidth={stroke} fill="none"
          strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
          className="transition-all duration-700 ease-out"
        />
        <defs>
          <linearGradient id="gp-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#0d9488" />
            <stop offset="100%" stopColor="#06b6d4" />
          </linearGradient>
        </defs>
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-bold text-teal-800">{percent}%</span>
        <span className="text-[10px] text-slate-400 uppercase tracking-wide">Complete</span>
      </div>
    </div>
  );
};

const VersePicker = ({ verses, onAdd, onRemove, testidPrefix = 'scripture' }) => {
  const [query, setQuery] = useState('');
  const [translation, setTranslation] = useState('esv');
  const [loading, setLoading] = useState(false);

  const searchAndAdd = async () => {
    const ref = query.trim();
    if (!ref) return;
    setLoading(true);
    try {
      const t = SCRIPTURE_TRANSLATIONS.find((x) => x.id === translation);
      let data;
      if (t.source === 'esv-api') {
        const res = await axios.get(`${API}/bible/esv?q=${encodeURIComponent(ref)}`);
        data = res.data;
        if (data.detail) throw new Error('not_found');
      } else {
        const res = await fetch(`https://bible-api.com/${encodeURIComponent(ref.replace(/\s+/g, ' '))}?translation=${translation}`);
        data = await res.json();
        if (data.error) throw new Error('not_found');
      }
      const text = cleanVerseText(data);
      if (!text) throw new Error('not_found');
      onAdd({ reference: data.reference || data.canonical || ref, text, translation: t.label });
      setQuery('');
    } catch (e) {
      toast.error('Verse not found. Try "John 3:16" or "Psalm 23".');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); searchAndAdd(); } }}
          placeholder='Search a reference — e.g. "John 3:16"'
          data-testid={`${testidPrefix}-search-input`}
        />
        <select
          value={translation}
          onChange={(e) => setTranslation(e.target.value)}
          className="px-2 text-sm border border-slate-200 rounded-md bg-white shrink-0"
          data-testid={`${testidPrefix}-translation-select`}
        >
          {SCRIPTURE_TRANSLATIONS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
        <Button type="button" onClick={searchAndAdd} disabled={loading || !query.trim()} size="sm" className="shrink-0" data-testid={`${testidPrefix}-add-btn`}>
          {loading ? '...' : <><Plus className="w-4 h-4 mr-1" /> Add</>}
        </Button>
      </div>
      {verses.length > 0 && (
        <div className="space-y-1.5">
          {verses.map((v, i) => (
            <div key={i} className="flex items-start gap-2 bg-teal-50 border border-teal-100 rounded-md px-3 py-2" data-testid={`${testidPrefix}-item-${i}`}>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-slate-600 italic">"{v.text}"</p>
                <p className="text-xs font-semibold text-teal-700 mt-0.5">— {v.reference} ({v.translation})</p>
              </div>
              <button type="button" onClick={() => onRemove(i)} className="text-slate-400 hover:text-red-500 shrink-0" data-testid={`${testidPrefix}-remove-${i}`}>
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const StageJournals = ({ stageId, refreshKey }) => {
  const [journals, setJournals] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    setJournals(null);
    axios.get(`${API}/growth-plan/stages/${stageId}/journals`)
      .then(res => setJournals(res.data))
      .catch(() => setJournals([]));
  }, [stageId, refreshKey]);

  if (journals === null) return <p className="text-sm text-slate-400">Loading journal entries...</p>;
  if (journals.length === 0) return <p className="text-sm text-slate-400 italic">No journal entries linked yet.</p>;

  return (
    <div className="space-y-2.5" data-testid={`stage-journals-${stageId}`}>
      {journals.map(j => (
        <button
          key={j.journal_id}
          onClick={() => navigate(`/journals/${j.journal_id}`)}
          className="flex items-center gap-3 w-full text-left p-4 rounded-xl bg-white hover:bg-teal-50 border-2 border-teal-200 hover:border-teal-400 shadow-sm hover:shadow-md transition-all"
          data-testid={`stage-journal-link-${j.journal_id}`}
        >
          <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center shrink-0">
            <BookOpen className="w-5 h-5 text-teal-600" />
          </div>
          <span className="text-base font-semibold text-slate-900 truncate flex-1">{j.title}</span>
          <Badge variant="outline" className="text-xs shrink-0">{j.status}</Badge>
          <ChevronRight className="w-5 h-5 text-teal-400 shrink-0" />
        </button>
      ))}
    </div>
  );
};

const StageSection = ({ icon: Icon, label, iconColor, children }) => (
  <div>
    <p className={`text-xs font-semibold uppercase tracking-wide mb-1 flex items-center gap-1.5 ${iconColor || 'text-slate-400'}`}>
      <Icon className="w-3.5 h-3.5" /> {label}
    </p>
    {children}
  </div>
);

const VerseRefsList = ({ verses, colorClass = 'bg-teal-50 border-teal-100 text-teal-700', testidPrefix }) => (
  <div className="space-y-1.5">
    {verses.map((v, i) => (
      <div key={i} className={`rounded-md px-3 py-2 border ${colorClass}`} data-testid={testidPrefix ? `${testidPrefix}-${i}` : undefined}>
        <p className="text-sm italic text-slate-700">"{v.text}"</p>
        <p className="text-xs font-semibold mt-0.5">— {v.reference} ({v.translation})</p>
      </div>
    ))}
  </div>
);

const ACTION_TYPE_META = {
  create_journal_entry: { icon: BookOpen, defaultLabel: 'Create Journal Entry' },
  read_bible: { icon: BookMarked, defaultLabel: 'Read the Bible' },
  watch_video: { icon: Video, defaultLabel: 'Watch Video' },
  listen_audio: { icon: Music, defaultLabel: 'Listen to Audio' },
};

const DEFAULT_ACTIONS = [
  { action_type: 'create_journal_entry', enabled: true, label: 'Create Journal Entry', instructions: '' },
  { action_type: 'read_bible', enabled: false, label: 'Read the Bible', instructions: '', references: [] },
  { action_type: 'watch_video', enabled: false, label: 'Watch Video', instructions: '', video_url: '' },
  { action_type: 'listen_audio', enabled: false, label: 'Listen to Audio', instructions: '', audio_url: '' },
];

const YT_REGEX = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?(?:.*&)?v=|shorts\/|embed\/|live\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
const ytEmbed = (url) => {
  const m = url?.match(YT_REGEX);
  return m ? `https://www.youtube.com/embed/${m[1]}?rel=0&modestbranding=1` : null;
};
const isSafeMediaUrl = (url) => /^https?:\/\//i.test(url || '');

const mergeActionDefaults = (actions) => {
  const existingTypes = new Set((actions || []).map((a) => a.action_type));
  const merged = [...(actions || [])];
  DEFAULT_ACTIONS.forEach((d) => { if (!existingTypes.has(d.action_type)) merged.push({ ...d }); });
  return merged;
};

const TaskActionsDialog = ({ open, onOpenChange, actions, onSave }) => {
  const [draft, setDraft] = useState(actions);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setDraft(actions); }, [open, actions]);

  const updateAction = (idx, field, value) =>
    setDraft((prev) => prev.map((a, i) => (i === idx ? { ...a, [field]: value } : a)));

  const handleSave = async () => {
    setSaving(true);
    await onSave(draft);
    setSaving(false);
    onOpenChange(false);
  };

  const enabledCount = draft.filter((a) => a.enabled).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg p-0 gap-0 max-h-[85vh] flex flex-col"
        data-testid="task-actions-dialog"
      >
        <DialogHeader className="px-6 pt-6 pb-4 shrink-0 border-b border-slate-100">
          <DialogTitle className="flex items-center gap-1.5">
            <Settings2 className="w-4 h-4 text-orange-600" /> Manage Task Actions
          </DialogTitle>
          <DialogDescription>
            Choose what the student can do to accomplish this stage's task
            {enabledCount > 0 && <span className="text-orange-600 font-medium"> — {enabledCount} enabled</span>}.
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {draft.map((a, i) => {
            const meta = ACTION_TYPE_META[a.action_type] || {};
            const Icon = meta.icon || Zap;
            return (
              <div
                key={i}
                className={`border rounded-lg transition-colors ${a.enabled ? 'border-orange-200 bg-orange-50/40' : 'border-slate-200'}`}
                data-testid={`task-action-row-${i}`}
              >
                <div className="flex items-center justify-between px-3 py-2.5">
                  <span className={`text-sm font-medium flex items-center gap-1.5 ${a.enabled ? 'text-slate-900' : 'text-slate-500'}`}>
                    <Icon className={`w-3.5 h-3.5 ${a.enabled ? 'text-orange-600' : 'text-slate-400'}`} /> {meta.defaultLabel || a.action_type}
                  </span>
                  <Switch
                    checked={a.enabled}
                    onCheckedChange={(v) => updateAction(i, 'enabled', v)}
                    data-testid={`task-action-toggle-${i}`}
                  />
                </div>
                {a.enabled && (
                  <div className="px-3 pb-3 space-y-2 border-t border-orange-100 pt-2.5">
                    <Input
                      value={a.label}
                      onChange={(e) => updateAction(i, 'label', e.target.value)}
                      placeholder="Button label shown to the student"
                      data-testid={`task-action-label-${i}`}
                    />
                    {a.action_type === 'read_bible' && (
                      <VersePicker
                        verses={a.references || []}
                        onAdd={(v) => updateAction(i, 'references', [...(a.references || []), v])}
                        onRemove={(idx) => updateAction(i, 'references', (a.references || []).filter((_, x) => x !== idx))}
                        testidPrefix={`task-action-reference-${i}`}
                      />
                    )}
                    {a.action_type === 'watch_video' && (
                      <Input
                        value={a.video_url || ''}
                        onChange={(e) => updateAction(i, 'video_url', e.target.value)}
                        placeholder="Video URL — e.g. a YouTube link"
                        data-testid={`task-action-video-url-${i}`}
                      />
                    )}
                    {a.action_type === 'listen_audio' && (
                      <Input
                        value={a.audio_url || ''}
                        onChange={(e) => updateAction(i, 'audio_url', e.target.value)}
                        placeholder="Audio URL — e.g. an mp3 or podcast link"
                        data-testid={`task-action-audio-url-${i}`}
                      />
                    )}
                    <Textarea
                      value={a.instructions}
                      onChange={(e) => updateAction(i, 'instructions', e.target.value)}
                      placeholder="Optional instructions shown to the student before they start"
                      className="min-h-[56px]"
                      data-testid={`task-action-instructions-${i}`}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <DialogFooter className="px-6 py-4 shrink-0 border-t border-slate-100">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-orange-600 hover:bg-orange-700" data-testid="task-actions-save-btn">
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ReadBibleDialog = ({ open, onOpenChange, loading, verses, completed, canToggle, onToggleComplete }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent data-testid="read-bible-dialog">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-1.5">
          <BookMarked className="w-4 h-4 text-teal-600" /> {verses.length > 1 ? `${verses.length} Passages` : (verses[0]?.reference || 'Scripture')}
        </DialogTitle>
        <DialogDescription>
          {verses.length > 0 ? [...new Set(verses.map((v) => v.translation).filter(Boolean))].join(', ') || 'Scripture' : 'Scripture'}
        </DialogDescription>
      </DialogHeader>
      {loading ? (
        <p className="text-sm text-slate-400 py-6 text-center">Loading passage{verses.length !== 1 ? 's' : ''}...</p>
      ) : verses.length > 0 ? (
        <div className="space-y-3 max-h-[50vh] overflow-y-auto">
          {verses.map((v, i) => (
            v.text ? (
              <div key={i} className="bg-teal-50 border border-teal-100 rounded-lg p-4" data-testid={`read-bible-passage-${i}`}>
                <p className="text-base text-slate-800 italic whitespace-pre-wrap leading-relaxed" data-testid={`read-bible-verse-text-${i}`}>"{v.text}"</p>
                <p className="text-sm font-semibold text-teal-700 mt-3" data-testid={`read-bible-reference-${i}`}>— {v.reference} ({v.translation || 'ESV'})</p>
              </div>
            ) : (
              <p key={i} className="text-sm text-red-500 text-center py-2" data-testid={`read-bible-error-${i}`}>Couldn't load "{v.reference}". Try again shortly.</p>
            )
          ))}
        </div>
      ) : (
        <p className="text-sm text-red-500 py-6 text-center">Couldn't load this passage. Try again shortly.</p>
      )}
      <DialogFooter className="flex-row items-center justify-between sm:justify-between">
        {canToggle && (
          <Button
            variant={completed ? "outline" : "default"}
            className={completed ? "border-teal-300 text-teal-700 hover:bg-teal-50" : "bg-teal-600 hover:bg-teal-700"}
            onClick={onToggleComplete}
            data-testid="read-bible-mark-complete-btn"
          >
            <CheckCircle2 className="w-4 h-4 mr-1.5" /> {completed ? "Marked Complete" : "Mark Task Complete"}
          </Button>
        )}
        <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="read-bible-close-btn">Close</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

const WatchVideoDialog = ({ open, onOpenChange, videoUrl }) => {
  const embedUrl = ytEmbed(videoUrl);
  const safeUrl = isSafeMediaUrl(videoUrl) ? videoUrl : null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="watch-video-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-1.5"><Video className="w-4 h-4 text-orange-600" /> Watch Video</DialogTitle>
        </DialogHeader>
        {embedUrl ? (
          <div className="aspect-video w-full rounded-lg overflow-hidden bg-black">
            <iframe
              src={embedUrl}
              title="Task video"
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              data-testid="watch-video-embed"
            />
          </div>
        ) : safeUrl ? (
          <video controls className="w-full rounded-lg" src={safeUrl} data-testid="watch-video-native">
            Your browser doesn't support embedded video.
            <a href={safeUrl} target="_blank" rel="noreferrer">Open the video</a>
          </video>
        ) : (
          <p className="text-sm text-red-500 py-6 text-center">No video URL set for this task yet.</p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="watch-video-close-btn">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ListenAudioDialog = ({ open, onOpenChange, audioUrl }) => {
  const safeUrl = isSafeMediaUrl(audioUrl) ? audioUrl : null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="listen-audio-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-1.5"><Music className="w-4 h-4 text-orange-600" /> Listen to Audio</DialogTitle>
        </DialogHeader>
        {safeUrl ? (
          <div className="bg-orange-50 border border-orange-100 rounded-lg p-4">
            <audio controls className="w-full" src={safeUrl} data-testid="listen-audio-player">
              Your browser doesn't support embedded audio.
            </audio>
            <a href={safeUrl} target="_blank" rel="noreferrer" className="text-xs text-orange-600 underline mt-2 inline-block">Open in a new tab</a>
          </div>
        ) : (
          <p className="text-sm text-red-500 py-6 text-center">No audio URL set for this task yet.</p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="listen-audio-close-btn">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


const TaskRow = ({ task: t, stageId, canManage, canProgress, isStudentOwner, onToggleTask, onManageActions, onJournalAction, onReadBibleAction, onWatchVideoAction, onListenAudioAction }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: t.task_id,
    disabled: !canManage,
  });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  const enabledActions = (t.actions || []).filter((a) => a.enabled);

  return (
    <div ref={setNodeRef} style={style} className="bg-white/70 rounded-lg p-3 border border-orange-200" data-testid={`stage-task-${t.task_id}`}>
      <div className="flex items-start gap-2.5">
        {canManage && (
          <button
            {...attributes}
            {...listeners}
            className="mt-0.5 shrink-0 text-orange-300 hover:text-orange-500 cursor-grab active:cursor-grabbing"
            data-testid={`stage-task-drag-handle-${t.task_id}`}
            title="Drag to reorder"
          >
            <GripVertical className="w-4 h-4" />
          </button>
        )}
        <button
          onClick={() => canProgress && onToggleTask(stageId, t.task_id, !t.is_completed)}
          disabled={!canProgress}
          data-testid={`stage-task-toggle-${t.task_id}`}
          className={`mt-0.5 shrink-0 ${canProgress ? 'cursor-pointer hover:scale-110 transition-transform' : 'cursor-default'}`}
          title={canProgress ? (t.is_completed ? 'Mark as not done' : 'Mark as done') : ''}
        >
          {t.is_completed
            ? <CheckCircle2 className="w-5 h-5 text-orange-600" />
            : <Circle className="w-5 h-5 text-orange-300" />}
        </button>
        <p className={`text-sm flex-1 whitespace-pre-wrap leading-relaxed ${t.is_completed ? 'text-slate-400 line-through' : 'text-slate-900 font-medium'}`}>{t.text}</p>
        {canManage && (
          <Button
            size="icon"
            variant="ghost"
            className="h-6 w-6 shrink-0"
            onClick={() => onManageActions(t)}
            data-testid={`stage-manage-task-actions-${t.task_id}`}
            title="Manage this task's action button"
          >
            <Settings2 className="w-3.5 h-3.5 text-orange-500" />
          </Button>
        )}
      </div>
      {isStudentOwner && enabledActions.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2 ml-7">
          {enabledActions.map((action, i) => {
            const ActionIcon = ACTION_TYPE_META[action.action_type]?.icon || Zap;
            return (
              <Button
                key={i}
                size="sm"
                className="bg-orange-600 hover:bg-orange-700"
                onClick={() => {
                  if (action.action_type === 'create_journal_entry') onJournalAction(t, action);
                  else if (action.action_type === 'read_bible') onReadBibleAction(t, action);
                  else if (action.action_type === 'watch_video') onWatchVideoAction(action);
                  else if (action.action_type === 'listen_audio') onListenAudioAction(action);
                }}
                data-testid={`stage-task-action-${action.action_type}-${t.task_id}`}
              >
                <ActionIcon className="w-3.5 h-3.5 mr-1.5" /> {action.label || (ACTION_TYPE_META[action.action_type]?.defaultLabel) || 'Action'}
              </Button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const StageCard = ({ stage, index, canManage, canProgress, isStudentOwner, onToggle, onToggleTask, onReorderTasks, onSaveTaskActions, onEdit, onDelete, onAddJournal, autoExpand }) => {
  const [expanded, setExpanded] = useState(false);
  const [taskJournalOpen, setTaskJournalOpen] = useState(false);
  const [journalTitle, setJournalTitle] = useState('');
  const [journalContent, setJournalContent] = useState('');
  const [activeInstructions, setActiveInstructions] = useState('');
  const [savingTaskJournal, setSavingTaskJournal] = useState(false);
  const [journalsRefreshKey, setJournalsRefreshKey] = useState(0);
  const [taskActionsFor, setTaskActionsFor] = useState(null);
  const [bibleDialogOpen, setBibleDialogOpen] = useState(false);
  const [bibleLoading, setBibleLoading] = useState(false);
  const [bibleVerses, setBibleVerses] = useState([]);
  const [bibleTaskId, setBibleTaskId] = useState(null);
  const [bibleTaskCompleted, setBibleTaskCompleted] = useState(false);
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const [activeVideoUrl, setActiveVideoUrl] = useState('');
  const [audioDialogOpen, setAudioDialogOpen] = useState(false);
  const [activeAudioUrl, setActiveAudioUrl] = useState('');
  const cardRef = useRef(null);
  const taskSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: stage.stage_id,
    disabled: !canManage,
  });
  const dragStyle = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  const setRefs = useCallback((node) => { cardRef.current = node; setNodeRef(node); }, [setNodeRef]);

  useEffect(() => {
    if (autoExpand) {
      setExpanded(true);
      setTimeout(() => cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
    }
  }, [autoExpand]);

  const openTaskJournal = (task, action) => {
    setJournalTitle(`${stage.title} Reflection`);
    setJournalContent('');
    setActiveInstructions(action?.instructions || '');
    setTaskJournalOpen(true);
  };

  const openReadBible = async (task, action) => {
    const refs = action.references || [];
    if (refs.length === 0) {
      toast.error("Your teacher hasn't added any scripture references for this task yet");
      return;
    }
    setBibleTaskId(task.task_id);
    setBibleTaskCompleted(task.is_completed);
    setBibleVerses([]);
    setBibleLoading(true);
    setBibleDialogOpen(true);
    try {
      const resolved = await Promise.all(refs.map(async (r) => {
        if (r.text) return r;
        try {
          const res = await axios.get(`${API}/bible/esv?q=${encodeURIComponent(r.reference)}`);
          return { reference: res.data.reference, text: res.data.text, translation: 'ESV' };
        } catch (e) {
          return { ...r, text: null };
        }
      }));
      setBibleVerses(resolved);
    } finally {
      setBibleLoading(false);
    }
  };

  const handleToggleBibleTaskComplete = () => {
    const next = !bibleTaskCompleted;
    setBibleTaskCompleted(next);
    onToggleTask(stage.stage_id, bibleTaskId, next);
  };

  const openWatchVideo = (action) => {
    if (!action.video_url?.trim()) {
      toast.error("Your teacher hasn't set a video for this task yet");
      return;
    }
    setActiveVideoUrl(action.video_url);
    setVideoDialogOpen(true);
  };

  const openListenAudio = (action) => {
    if (!action.audio_url?.trim()) {
      toast.error("Your teacher hasn't set an audio link for this task yet");
      return;
    }
    setActiveAudioUrl(action.audio_url);
    setAudioDialogOpen(true);
  };

  const submitTaskJournal = async (status) => {
    if (!journalTitle.trim() || !journalContent.trim()) {
      toast.error("Please add a title and content");
      return;
    }
    setSavingTaskJournal(true);
    try {
      await axios.post(`${API}/journals`, {
        title: journalTitle,
        content: journalContent,
        status,
        growth_stage_id: stage.stage_id,
      });
      toast.success(status === 'submitted' ? "Journal submitted!" : "Journal saved as draft");
      setTaskJournalOpen(false);
      setJournalsRefreshKey((k) => k + 1);
      setExpanded(true);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to create journal entry");
    } finally {
      setSavingTaskJournal(false);
    }
  };

  const tasks = stage.tasks || [];
  const taskProgress = stage.task_progress || { total: tasks.length, completed: 0, percent: 0 };
  const allTasksDone = tasks.length === 0 || tasks.every((t) => t.is_completed);
  const lockedByTasks = canProgress && !stage.is_completed && !allTasksDone;

  const handleTaskDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = tasks.findIndex((t) => t.task_id === active.id);
    const newIndex = tasks.findIndex((t) => t.task_id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(tasks, oldIndex, newIndex);
    onReorderTasks(stage.stage_id, reordered.map((t) => t.task_id));
  };

  return (
    <Card
      ref={setRefs}
      style={dragStyle}
      className={`border-slate-100 transition-all ${stage.is_completed ? 'bg-teal-50/40 border-teal-200' : ''} ${autoExpand ? 'ring-2 ring-cyan-400' : ''}`}
      data-testid={`growth-stage-card-${stage.stage_id}`}
    >
      <CardContent className="p-5">
        <div className="flex items-start gap-4">
          <button
            onClick={() => canProgress && allTasksDone && onToggle(stage)}
            disabled={!canProgress || !allTasksDone}
            data-testid={`stage-toggle-complete-${stage.stage_id}`}
            className={`relative mt-0.5 shrink-0 ${canProgress && allTasksDone ? 'cursor-pointer hover:scale-110 transition-transform' : 'cursor-default'}`}
            title={lockedByTasks ? 'Complete all tasks before marking this stage complete' : (canProgress ? (stage.is_completed ? 'Mark as not started' : 'Mark complete') : '')}
          >
            {stage.is_completed
              ? <CheckCircle2 className="w-7 h-7 text-teal-600" />
              : <Circle className={`w-7 h-7 ${lockedByTasks ? 'text-slate-200' : 'text-slate-300'}`} />}
            {lockedByTasks && (
              <Lock className="w-3 h-3 text-slate-400 absolute -bottom-0.5 -right-0.5 bg-white rounded-full" />
            )}
          </button>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-teal-600 bg-teal-100 rounded-full px-2 py-0.5">Stage {index + 1}</span>
                <h3 className={`font-semibold text-slate-900 ${stage.is_completed ? 'text-teal-800' : ''}`}>{stage.title}</h3>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {canManage && (
                  <>
                    <button
                      {...attributes}
                      {...listeners}
                      className="h-7 w-7 flex items-center justify-center text-slate-400 hover:text-slate-600 cursor-grab active:cursor-grabbing"
                      data-testid={`stage-drag-handle-${stage.stage_id}`}
                      title="Drag to reorder"
                    >
                      <GripVertical className="w-4 h-4" />
                    </button>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onEdit(stage)} data-testid={`stage-edit-${stage.stage_id}`}>
                      <Pencil className="w-3.5 h-3.5 text-slate-500" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onDelete(stage)} data-testid={`stage-delete-${stage.stage_id}`}>
                      <Trash2 className="w-3.5 h-3.5 text-red-400" />
                    </Button>
                  </>
                )}
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setExpanded(!expanded)} data-testid={`stage-expand-${stage.stage_id}`}>
                  {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            {stage.is_completed && (
              <p className="text-xs text-teal-600 mt-1">
                Completed {stage.completed_at ? format(new Date(stage.completed_at), 'MMM d, yyyy') : ''}{stage.completed_by_name ? ` by ${stage.completed_by_name}` : ''}
              </p>
            )}

            {!expanded && (
              <div className="mt-1.5 space-y-2">
                <p className="text-sm text-slate-500 line-clamp-2 whitespace-pre-wrap">{stage.objective}</p>
                {tasks.length > 0 && (
                  <div className="flex items-center gap-2" data-testid={`stage-task-preview-${stage.stage_id}`}>
                    <div className="h-1.5 flex-1 max-w-[160px] rounded-full bg-orange-100 overflow-hidden">
                      <div className="h-full bg-orange-500 transition-all duration-500" style={{ width: `${taskProgress.percent}%` }} />
                    </div>
                    <span className="text-xs font-medium text-orange-700">{taskProgress.completed}/{taskProgress.total} tasks</span>
                  </div>
                )}
              </div>
            )}

            {expanded && (
              <div className="mt-3 space-y-4">
                <StageSection icon={Sparkles} label="Objective" iconColor="text-teal-500">
                  <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{stage.objective}</p>
                </StageSection>

                {stage.scripture_refs?.length > 0 && (
                  <StageSection icon={BookMarked} label="Scripture" iconColor="text-teal-500">
                    <VerseRefsList verses={stage.scripture_refs} testidPrefix={`stage-scripture-${stage.stage_id}`} />
                  </StageSection>
                )}

                {tasks.length > 0 && (
                  <div className="rounded-xl border-2 border-orange-300 bg-gradient-to-br from-orange-50 to-orange-100/60 p-4 shadow-sm space-y-3" data-testid={`stage-tasks-${stage.stage_id}`}>
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-bold uppercase tracking-wider text-orange-800 flex items-center gap-1.5">
                        <div className="w-5 h-5 rounded-full bg-orange-600 flex items-center justify-center">
                          <ClipboardList className="w-3 h-3 text-white" />
                        </div>
                        Tasks
                      </p>
                      <span className="text-xs font-semibold text-orange-700" data-testid={`stage-task-progress-label-${stage.stage_id}`}>
                        {taskProgress.completed}/{taskProgress.total} done
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-orange-200 overflow-hidden" data-testid={`stage-task-progress-bar-${stage.stage_id}`}>
                      <div className="h-full bg-orange-600 transition-all duration-500" style={{ width: `${taskProgress.percent}%` }} />
                    </div>
                    <DndContext sensors={taskSensors} collisionDetection={closestCenter} onDragEnd={handleTaskDragEnd}>
                      <SortableContext items={tasks.map((t) => t.task_id)} strategy={verticalListSortingStrategy}>
                        <div className="space-y-2.5">
                          {tasks.map((t) => (
                            <TaskRow
                              key={t.task_id}
                              task={t}
                              stageId={stage.stage_id}
                              canManage={canManage}
                              canProgress={canProgress}
                              isStudentOwner={isStudentOwner}
                              onToggleTask={onToggleTask}
                              onManageActions={setTaskActionsFor}
                              onJournalAction={openTaskJournal}
                              onReadBibleAction={openReadBible}
                              onWatchVideoAction={openWatchVideo}
                              onListenAudioAction={openListenAudio}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                  </div>
                )}

                <TaskActionsDialog
                  open={!!taskActionsFor}
                  onOpenChange={(o) => !o && setTaskActionsFor(null)}
                  actions={mergeActionDefaults(taskActionsFor?.actions)}
                  onSave={(actions) => onSaveTaskActions(stage.stage_id, taskActionsFor.task_id, actions)}
                />

                <ReadBibleDialog
                  open={bibleDialogOpen}
                  onOpenChange={setBibleDialogOpen}
                  loading={bibleLoading}
                  verses={bibleVerses}
                  completed={bibleTaskCompleted}
                  canToggle={canProgress}
                  onToggleComplete={handleToggleBibleTaskComplete}
                />

                <WatchVideoDialog
                  open={videoDialogOpen}
                  onOpenChange={setVideoDialogOpen}
                  videoUrl={activeVideoUrl}
                />

                <ListenAudioDialog
                  open={audioDialogOpen}
                  onOpenChange={setAudioDialogOpen}
                  audioUrl={activeAudioUrl}
                />

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-bold text-slate-600 uppercase tracking-wide flex items-center gap-1.5">
                      <BookOpen className="w-4 h-4 text-teal-500" /> Linked Journal Entries
                    </p>
                    {isStudentOwner && (
                      <Button size="sm" variant="outline" onClick={() => onAddJournal(stage)} data-testid={`stage-add-journal-${stage.stage_id}`}>
                        <Plus className="w-3.5 h-3.5 mr-1.5" /> Journal Entry
                      </Button>
                    )}
                  </div>
                  <StageJournals stageId={stage.stage_id} refreshKey={journalsRefreshKey} />
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>

      <Dialog open={taskJournalOpen} onOpenChange={setTaskJournalOpen}>
        <DialogContent className="sm:max-w-lg" data-testid={`task-journal-dialog-${stage.stage_id}`}>
          <DialogHeader>
            <DialogTitle>New Journal Entry</DialogTitle>
            <DialogDescription>Linked to Growth Plan stage "{stage.title}"</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {activeInstructions && (
              <div className="bg-orange-50 border border-orange-200 rounded-md px-3 py-2 text-sm text-orange-800" data-testid={`task-journal-instructions-${stage.stage_id}`}>
                {activeInstructions}
              </div>
            )}
            <Input
              value={journalTitle}
              onChange={(e) => setJournalTitle(e.target.value)}
              placeholder="Entry title"
              data-testid={`task-journal-title-input-${stage.stage_id}`}
            />
            <Textarea
              value={journalContent}
              onChange={(e) => setJournalContent(e.target.value)}
              placeholder="Write your reflection on this task..."
              className="min-h-[160px]"
              data-testid={`task-journal-content-input-${stage.stage_id}`}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTaskJournalOpen(false)} disabled={savingTaskJournal}>Cancel</Button>
            <Button variant="secondary" onClick={() => submitTaskJournal('draft')} disabled={savingTaskJournal} data-testid={`task-journal-save-draft-${stage.stage_id}`}>
              Save as Draft
            </Button>
            <Button onClick={() => submitTaskJournal('submitted')} disabled={savingTaskJournal} className="bg-orange-600 hover:bg-orange-700" data-testid={`task-journal-submit-${stage.stage_id}`}>
              Submit for Review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const emptyStageForm = { title: '', objective: '', scripture_refs: [], tasks: [] };

export default function GrowthPlan({ user }) {
  const { studentId: paramStudentId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const deepLinkStageId = searchParams.get('stage_id');
  const canManage = user.role === 'teacher' || user.is_super_admin;
  const targetStudentId = user.role === 'student' ? user.user_id : paramStudentId;
  const [viewMode, setViewMode] = useState('teacher');
  const previewingAsStudent = canManage && viewMode === 'student';
  const effectiveCanManage = canManage && !previewingAsStudent;
  const effectiveIsStudentOwner = user.role === 'student' || previewingAsStudent;

  useEffect(() => { setViewMode('teacher'); }, [targetStudentId]);

  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  const [stageDialogOpen, setStageDialogOpen] = useState(false);
  const [editingStage, setEditingStage] = useState(null);
  const [stageForm, setStageForm] = useState(emptyStageForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [importText, setImportText] = useState('');
  const [importing, setImporting] = useState(false);

  const fetchStudents = useCallback(async () => {
    setLoadingStudents(true);
    try {
      const res = await axios.get(`${API}/users/students`);
      setStudents(res.data);
    } catch (error) {
      toast.error("Failed to load students");
    } finally {
      setLoadingStudents(false);
    }
  }, []);

  const fetchPlan = useCallback(async () => {
    if (!targetStudentId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/growth-plan/${targetStudentId}`);
      setPlan(res.data);
    } catch (error) {
      toast.error("Failed to load Growth Plan");
    } finally {
      setLoading(false);
    }
  }, [targetStudentId]);

  useEffect(() => {
    if (targetStudentId) {
      fetchPlan();
    } else if (canManage) {
      fetchStudents();
    }
  }, [targetStudentId, canManage, fetchPlan, fetchStudents]);

  const openAddStage = () => {
    setEditingStage(null);
    setStageForm({ ...emptyStageForm, tasks: [{ text: '' }] });
    setImportText('');
    setStageDialogOpen(true);
  };

  const openEditStage = (stage) => {
    setEditingStage(stage);
    setStageForm({
      title: stage.title,
      objective: stage.objective,
      scripture_refs: stage.scripture_refs || [],
      tasks: (stage.tasks || []).map((t) => ({ ...t })),
    });
    setImportText('');
    setStageDialogOpen(true);
  };

  const updateForm = (field, value) => setStageForm((prev) => ({ ...prev, [field]: value }));

  const addTask = () => setStageForm((prev) => ({ ...prev, tasks: [...prev.tasks, { text: '' }] }));
  const updateTask = (idx, text) => setStageForm((prev) => ({ ...prev, tasks: prev.tasks.map((t, i) => (i === idx ? { ...t, text } : t)) }));
  const removeTask = (idx) => setStageForm((prev) => ({ ...prev, tasks: prev.tasks.filter((_, i) => i !== idx) }));

  const importFromText = async () => {
    if (!importText.trim()) {
      toast.error("Paste some text first");
      return;
    }
    setImporting(true);
    try {
      const res = await axios.post(`${API}/growth-plan/parse-stage-text`, { text: importText });
      const d = res.data;
      setStageForm((prev) => ({
        title: d.title || prev.title,
        objective: d.objective || prev.objective,
        tasks: [...prev.tasks, ...((d.tasks || []).map((text) => ({ text })))],
        scripture_refs: [...prev.scripture_refs, ...(d.scripture_refs || [])],
      }));
      toast.success("Stage fields filled in — review before saving");
      if (d.skipped_references?.length) {
        toast.error(`Couldn't find verse text for: ${d.skipped_references.join(', ')}`);
      }
    } catch (error) {
      toast.error("Failed to import from text");
    } finally {
      setImporting(false);
    }
  };

  const saveStage = async () => {
    if (!stageForm.title.trim() || !stageForm.objective.trim()) {
      toast.error("Please fill in both title and objective");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...stageForm, tasks: stageForm.tasks.filter((t) => t.text.trim()) };
      if (editingStage) {
        await axios.put(`${API}/growth-plan/stages/${editingStage.stage_id}`, payload);
        toast.success("Stage updated");
      } else {
        await axios.post(`${API}/growth-plan/${targetStudentId}/stages`, payload);
        toast.success("Stage added");
      }
      setStageDialogOpen(false);
      fetchPlan();
    } catch (error) {
      toast.error("Failed to save stage");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/growth-plan/stages/${deleteTarget.stage_id}`);
      toast.success("Stage deleted");
      setDeleteTarget(null);
      fetchPlan();
    } catch (error) {
      toast.error("Failed to delete stage");
    }
  };

  const toggleComplete = async (stage) => {
    try {
      const res = await axios.put(`${API}/growth-plan/stages/${stage.stage_id}/complete`, { is_completed: !stage.is_completed });
      setPlan(prev => {
        const stages = prev.stages.map(s => s.stage_id === stage.stage_id ? res.data : s);
        const completed = stages.filter(s => s.is_completed).length;
        const total = stages.length;
        return { ...prev, stages, progress: { total, completed, percent: total ? Math.round((completed / total) * 100) : 0 } };
      });
      if (res.data.plan_completed) {
        toast.success("Growth Plan complete — every stage finished!", { duration: 6000, icon: <Trophy className="w-5 h-5 text-amber-500" /> });
      } else {
        toast.success(!stage.is_completed ? "Stage marked complete!" : "Stage reopened");
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to update stage");
    }
  };

  const toggleTask = async (stageId, taskId, isCompleted) => {
    try {
      const res = await axios.put(`${API}/growth-plan/stages/${stageId}/tasks/${taskId}/toggle`, { is_completed: isCompleted });
      setPlan(prev => ({ ...prev, stages: prev.stages.map(s => s.stage_id === stageId ? res.data : s) }));
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to update task");
    }
  };

  const saveTaskActions = async (stageId, taskId, actions) => {
    try {
      const res = await axios.put(`${API}/growth-plan/stages/${stageId}/tasks/${taskId}`, { actions });
      setPlan(prev => ({ ...prev, stages: prev.stages.map(s => s.stage_id === stageId ? res.data : s) }));
      toast.success("Task actions updated");
    } catch (error) {
      toast.error("Failed to update task actions");
    }
  };

  const reorderTasks = async (stageId, taskIds) => {
    setPlan(prev => ({
      ...prev,
      stages: prev.stages.map(s => {
        if (s.stage_id !== stageId) return s;
        const byId = Object.fromEntries((s.tasks || []).map(t => [t.task_id, t]));
        return { ...s, tasks: taskIds.map(id => byId[id]) };
      }),
    }));
    try {
      const res = await axios.put(`${API}/growth-plan/stages/${stageId}/tasks/reorder`, { task_ids: taskIds });
      setPlan(prev => ({ ...prev, stages: prev.stages.map(s => s.stage_id === stageId ? res.data : s) }));
    } catch (error) {
      toast.error("Failed to reorder tasks");
      fetchPlan();
    }
  };

  const stageSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleStageDragEnd = (event) => {
    const { active, over } = event;
    if (!plan || !over || active.id === over.id) return;
    const oldIndex = plan.stages.findIndex(s => s.stage_id === active.id);
    const newIndex = plan.stages.findIndex(s => s.stage_id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(plan.stages, oldIndex, newIndex);
    setPlan(prev => ({ ...prev, stages: reordered }));
    axios.put(`${API}/growth-plan/${targetStudentId}/reorder`, { stage_ids: reordered.map(s => s.stage_id) })
      .catch(() => { toast.error("Failed to reorder stages"); fetchPlan(); });
  };

  const addJournalFromStage = (stage) => {
    navigate(`/journals/new?growth_stage_id=${stage.stage_id}&stage_title=${encodeURIComponent(stage.title)}`);
  };

  // Teacher/admin with no student selected -> student picker
  if (!targetStudentId && canManage) {
    return (
      <Layout user={user}>
        <div className="space-y-6" data-testid="growth-plan-picker-page">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Route className="w-6 h-6 text-teal-600" /> Growth Plans
            </h1>
            <p className="text-slate-500 text-sm mt-1">Pick a student to view or manage their staged growth plan.</p>
          </div>
          {loadingStudents ? (
            <p className="text-slate-400 text-sm">Loading students...</p>
          ) : (
            <div className="grid gap-3">
              {students.map(s => (
                <Card
                  key={s.user_id}
                  className="border-slate-100 cursor-pointer hover:border-teal-300 transition-all card-hover"
                  onClick={() => navigate(`/growth-plan/${s.user_id}`)}
                  data-testid={`growth-plan-student-row-${s.user_id}`}
                >
                  <CardContent className="p-4 flex items-center gap-3">
                    <Avatar className="w-10 h-10">
                      <AvatarImage src={s.picture} />
                      <AvatarFallback className="bg-teal-100 text-teal-700">{getInitials(s.name)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{s.name}</p>
                      <p className="text-xs text-slate-500">{s.email}</p>
                    </div>
                    <Route className="w-4 h-4 text-slate-300" />
                  </CardContent>
                </Card>
              ))}
              {students.length === 0 && <p className="text-slate-400 text-sm">No students found.</p>}
            </div>
          )}
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="space-y-6" data-testid="growth-plan-page">
        {canManage && (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <button onClick={() => navigate('/growth-plan')} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-teal-600" data-testid="growth-plan-back-btn">
              <ArrowLeft className="w-4 h-4" /> Back to Students
            </button>
            {targetStudentId && (
              <div className="flex items-center gap-1 bg-slate-100 rounded-full p-1 w-fit" data-testid="growth-plan-view-toggle">
                <button
                  onClick={() => setViewMode('teacher')}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${viewMode === 'teacher' ? 'bg-white shadow-sm text-teal-700' : 'text-slate-500 hover:text-slate-700'}`}
                  data-testid="growth-plan-teacher-view-btn"
                >
                  Teacher View
                </button>
                <button
                  onClick={() => setViewMode('student')}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${viewMode === 'student' ? 'bg-white shadow-sm text-teal-700' : 'text-slate-500 hover:text-slate-700'}`}
                  data-testid="growth-plan-student-view-btn"
                >
                  Student View
                </button>
              </div>
            )}
          </div>
        )}

        {previewingAsStudent && (
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-lg px-3 py-2.5" data-testid="growth-plan-preview-banner">
            <Eye className="w-4 h-4 shrink-0" />
            Previewing as {plan?.student_name || 'the student'} would see it — actions here (checking tasks, creating journal entries) are real.
          </div>
        )}

        {loading ? (
          <p className="text-slate-400 text-sm">Loading Growth Plan...</p>
        ) : plan ? (
          <>
            <Card className={`transition-all ${plan.progress.total > 0 && plan.progress.percent === 100 ? 'border-amber-300 bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50' : 'border-teal-100 bg-gradient-to-br from-teal-50 to-cyan-50/50'}`}>
              <CardContent className="p-6 flex items-center gap-6 flex-wrap">
                <CircularProgress percent={plan.progress.percent} />
                <div className="flex-1 min-w-[200px]">
                  <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                    <Route className="w-6 h-6 text-teal-600" /> Growth Plan
                  </h1>
                  <p className="text-slate-600 mt-1">
                    {effectiveCanManage ? `${plan.student_name}'s journey` : 'Your personal growth journey'}
                  </p>
                  <p className="text-sm text-teal-700 font-medium mt-2" data-testid="growth-plan-progress-summary">
                    {plan.progress.completed} of {plan.progress.total} stages complete
                  </p>
                  {plan.progress.total > 0 && plan.progress.percent === 100 && (
                    <div className="flex items-center gap-2 mt-3 bg-amber-100 border border-amber-300 rounded-full px-3 py-1.5 w-fit" data-testid="growth-plan-complete-badge">
                      <Trophy className="w-4 h-4 text-amber-600" />
                      <span className="text-sm font-bold text-amber-800">
                        {effectiveCanManage ? `${plan.student_name} completed the whole plan!` : 'Growth Plan Complete!'}
                      </span>
                    </div>
                  )}
                </div>
                {effectiveCanManage && (
                  <Button onClick={openAddStage} className="bg-teal-600 hover:bg-teal-700" data-testid="add-growth-stage-btn">
                    <Plus className="w-4 h-4 mr-1.5" /> Add Stage
                  </Button>
                )}
              </CardContent>
            </Card>

            {plan.stages.length === 0 ? (
              <Card className="border-slate-100">
                <CardContent className="p-10 text-center">
                  <Sparkles className="w-10 h-10 text-teal-300 mx-auto mb-3" />
                  <p className="text-slate-500">
                    {effectiveCanManage
                      ? "No stages yet. Add the first stage to start this student's growth journey."
                      : "Your teacher hasn't added any growth stages yet."}
                  </p>
                  {effectiveCanManage && (
                    <Button onClick={openAddStage} variant="outline" className="mt-4" data-testid="add-first-growth-stage-btn">
                      <Plus className="w-4 h-4 mr-1.5" /> Add First Stage
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                <DndContext sensors={stageSensors} collisionDetection={closestCenter} onDragEnd={handleStageDragEnd}>
                  <SortableContext items={plan.stages.map((s) => s.stage_id)} strategy={verticalListSortingStrategy}>
                    {plan.stages.map((stage, i) => (
                      <StageCard
                        key={stage.stage_id}
                        stage={stage}
                        index={i}
                        canManage={effectiveCanManage}
                        canProgress={canManage || user.user_id === targetStudentId}
                        isStudentOwner={effectiveIsStudentOwner}
                        onToggle={toggleComplete}
                        onToggleTask={toggleTask}
                        onReorderTasks={reorderTasks}
                        onSaveTaskActions={saveTaskActions}
                        onEdit={openEditStage}
                        onDelete={setDeleteTarget}
                        onAddJournal={addJournalFromStage}
                        autoExpand={stage.stage_id === deepLinkStageId}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              </div>
            )}
          </>
        ) : null}
      </div>

      <Dialog open={stageDialogOpen} onOpenChange={setStageDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="growth-stage-dialog">
          <DialogHeader>
            <DialogTitle>{editingStage ? 'Edit Stage' : 'Add New Stage'}</DialogTitle>
            <DialogDescription>Define the objective, scripture and task for this stage.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-cyan-50 border border-cyan-100 rounded-lg p-3 space-y-2" data-testid="stage-import-section">
              <label className="text-sm font-medium text-cyan-800 flex items-center gap-1.5">
                <Wand2 className="w-3.5 h-3.5" /> Paste &amp; Auto-Fill
              </label>
              <Textarea
                value={importText}
                onChange={(e) => setImportText(e.target.value)}
                placeholder="Paste your freeform notes for this stage and AI will split them into the fields below, fetching any scripture verses mentioned..."
                className="text-sm min-h-[70px] bg-white"
                data-testid="stage-import-text-input"
              />
              <Button
                type="button"
                size="sm"
                onClick={importFromText}
                disabled={importing || !importText.trim()}
                className="bg-cyan-600 hover:bg-cyan-700"
                data-testid="stage-import-btn"
              >
                {importing ? 'Importing...' : <><Wand2 className="w-3.5 h-3.5 mr-1.5" /> Auto-Fill from Text</>}
              </Button>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Stage Title</label>
              <Input value={stageForm.title} onChange={(e) => updateForm('title', e.target.value)} placeholder="e.g. Foundations of Faith" data-testid="growth-stage-title-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-teal-500" /> Objective</label>
              <Textarea
                value={stageForm.objective}
                onChange={(e) => updateForm('objective', e.target.value)}
                placeholder="What is the goal of this stage?"
                className="min-h-[80px]"
                data-testid="growth-stage-objective-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 flex items-center gap-1.5"><BookMarked className="w-3.5 h-3.5 text-teal-500" /> Scripture</label>
              <VersePicker
                verses={stageForm.scripture_refs}
                onAdd={(v) => updateForm('scripture_refs', [...stageForm.scripture_refs, v])}
                onRemove={(idx) => updateForm('scripture_refs', stageForm.scripture_refs.filter((_, i) => i !== idx))}
                testidPrefix="scripture"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 flex items-center gap-1.5"><ClipboardList className="w-3.5 h-3.5 text-orange-600" /> Tasks</label>
              <div className="space-y-2">
                {stageForm.tasks.map((t, i) => (
                  <div key={t.task_id || `new-${i}`} className="flex items-start gap-2" data-testid={`growth-stage-task-row-${i}`}>
                    <Textarea
                      value={t.text}
                      onChange={(e) => updateTask(i, e.target.value)}
                      placeholder={`Task ${i + 1} — what should the student do?`}
                      className="min-h-[50px]"
                      data-testid={`growth-stage-task-input-${i}`}
                    />
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 mt-1 shrink-0"
                      onClick={() => removeTask(i)}
                      data-testid={`growth-stage-task-remove-${i}`}
                    >
                      <X className="w-4 h-4 text-red-400" />
                    </Button>
                  </div>
                ))}
              </div>
              <Button type="button" size="sm" variant="outline" className="mt-2" onClick={addTask} data-testid="growth-stage-task-add-btn">
                <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Task
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStageDialogOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={saveStage} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="growth-stage-save-btn">
              {saving ? 'Saving...' : editingStage ? 'Save Changes' : 'Add Stage'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="Delete this stage?"
        description={`"${deleteTarget?.title}" will be permanently removed. Linked journal entries will not be deleted.`}
      />
    </Layout>
  );
}
